import { isSameDay } from 'date-fns'
import { useMemo, useReducer } from 'react'

/** The tab an item panel is showing. A moment with no content in view has only the first. */
export type CalendarPanelTab = 'settings' | 'content'

/**
 * One item along the path, and the tab a person last chose on it. The tab rides
 * on the step because the panel itself is taken down while something opened
 * inside it is on screen, and put back up again on the way out.
 */
export type CalendarPanelItemStep = Readonly<{
  type: 'item'
  id: string
  back?: CalendarPanelStep
  tab?: CalendarPanelTab
}>

/**
 * One step along the path a person took into the calendar: the day they opened,
 * and every item they opened from there. `back` is the step before it, so the
 * panel can walk back out the way it came rather than always landing on a day.
 *
 * An item with no `back` was opened straight off the grid, which no step leads
 * out of.
 */
export type CalendarPanelStep =
  | Readonly<{ type: 'day'; date: Date }>
  | CalendarPanelItemStep

/**
 * What the panel is showing, or `null` when it is closed.
 *
 * One value rather than a flag per panel, because the panel only ever shows one
 * thing. That makes "the filter drawer wins over the panels" true by
 * construction instead of by a guard on every render.
 */
export type CalendarPanelState =
  | null
  | Readonly<{ type: 'filters' }>
  | CalendarPanelStep

/** The path back out of a step, itself first and the day it started on last. */
const pathOut = (step: CalendarPanelStep): CalendarPanelStep[] =>
  step.type === 'day' || step.back === undefined
    ? [step]
    : [step, ...pathOut(step.back)]

/**
 * How far down the path the panel is, or nothing at all when it is closed. The
 * view slides deeper panels in from one side and shallower ones from the other,
 * and leaves a panel that swaps at the same depth where it is.
 *
 * An item sits one below whatever it was opened from. One opened straight off
 * the grid sits where an item off a day sits: it is one thing off a list,
 * whether or not the list was ever on screen.
 */
export const calendarPanelDepth = (
  panel: CalendarPanelState
): number | undefined => {
  if (panel === null) return undefined
  return panel.type === 'filters' ? 0 : stepDepth(panel)
}

const stepDepth = (step: CalendarPanelStep): number =>
  step.type === 'day'
    ? 1
    : 1 + (step.back === undefined ? 1 : stepDepth(step.back))

/**
 * Whether the panel has anything to draw. It is shut, and takes no width, when
 * it has not: an item panel outlives the item it was opened for, which a filter,
 * the Show set or a re-fetch can take away underneath it.
 *
 * A host that makes room for the panel has to ask this same question, or it keeps
 * making room for a panel that is no longer there.
 */
export const isCalendarPanelShowing = (
  panel: CalendarPanelState,
  {
    hasFilters,
    items,
  }: { hasFilters: boolean; items: readonly { id: string }[] }
): boolean => {
  switch (panel?.type) {
    case 'filters':
      return hasFilters
    case 'day':
      return true
    case 'item':
      return items.some((each) => each.id === panel.id)
    default:
      return false
  }
}

export type CalendarPanelAction =
  | Readonly<{ type: 'close' }>
  | Readonly<{ type: 'openDay'; date: Date }>
  | Readonly<{
      type: 'openItem'
      id: string
      back?: CalendarPanelStep
      tab?: CalendarPanelTab
    }>
  | Readonly<{ type: 'toggleFilters' }>

export const calendarPanelReducer = (
  state: CalendarPanelState,
  action: CalendarPanelAction
): CalendarPanelState => {
  switch (action.type) {
    case 'close':
      return null
    case 'toggleFilters':
      return state?.type === 'filters' ? null : { type: 'filters' }
    case 'openDay':
      // Asking for the day already on show closes the panel, so one control both
      // opens a day and puts it away.
      return state?.type === 'day' && isSameDay(state.date, action.date)
        ? null
        : { type: 'day', date: action.date }
    case 'openItem': {
      // Hopping between a moment and its content would stack up for ever
      // otherwise. An item already on the path is gone back to, not opened
      // again, which also keeps the way out of it the one it already had.
      const alreadyOpen =
        state === null || state.type === 'filters'
          ? undefined
          : pathOut(state).find(
              (each): each is CalendarPanelItemStep =>
                each.type === 'item' && each.id === action.id
            )

      const step = alreadyOpen ?? {
        type: 'item' as const,
        id: action.id,
        back: action.back,
      }

      // Only a caller that names a tab moves the item off the tab it carries, so
      // going back to an item comes out where it was left and stepping on to
      // another item starts that one afresh.
      return action.tab === undefined ? step : { ...step, tab: action.tab }
    }
  }
}

export type UseCalendarPanelValue = Readonly<{
  closePanel: () => void
  openDay: (date: Date) => void
  /**
   * `back` is the panel to return to, when the item was opened from one, and
   * `tab` the tab to show the item on. Without a tab the item keeps whichever
   * one it already carries, and a fresh item opens on its first.
   */
  openItem: (
    id: string,
    options?: { back?: CalendarPanelStep; tab?: CalendarPanelTab }
  ) => void
  panel: CalendarPanelState
  toggleFilters: () => void
}>

/**
 * Drives `<CalendarPanel>`. An adapter holds this and hands the callbacks to the
 * grid: `onDayOpen` and `onOverflowClick` to `openDay`, `onItemClick` to
 * `openItem`, `onEscape` to `closePanel`.
 */
export const useCalendarPanel = (): UseCalendarPanelValue => {
  const [panel, dispatch] = useReducer(calendarPanelReducer, null)

  return useMemo(
    () => ({
      closePanel: () => dispatch({ type: 'close' }),
      openDay: (date: Date) => dispatch({ type: 'openDay', date }),
      openItem: (
        id: string,
        options?: { back?: CalendarPanelStep; tab?: CalendarPanelTab }
      ) =>
        dispatch({
          type: 'openItem',
          back: options?.back,
          id,
          tab: options?.tab,
        }),
      panel,
      toggleFilters: () => dispatch({ type: 'toggleFilters' }),
    }),
    [panel]
  )
}
